import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { useEntities } from "@/lib/queries";
import { api, errMsg } from "@/lib/api";

const Check = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2.5 text-sm text-[#F3EBDD]"><Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} />{label}</label>
);

export const StoryExportDialog = ({ story, draftId, uid, onClose }) => {
  const commercial = story.tier === "commercial";
  const { data: ents = [], isLoading: entsLoading } = useEntities(uid);
  const [format, setFormat] = useState("pdf");
  const [draft, setDraft] = useState(draftId);
  const [lore, setLore] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [appx, setAppx] = useState([]);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/stories/${story.id}/export`, { format, draft_id: draft, include_lore: lore, include_hidden: hidden, appendix_ids: appx }, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `story.${format}`;
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
      onClose();
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : errMsg(e);
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="story-export-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Export "{story.title}"</DialogTitle><DialogDescription>Title page, contents, every chapter in storyboard order, and footnotes at the end of each chapter. Scene placeholders are left out.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Select value={format} onValueChange={setFormat}><SelectTrigger data-testid="export-format"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="epub">EPUB</SelectItem></SelectContent></Select>
          <Select value={draft} onValueChange={setDraft}><SelectTrigger data-testid="export-draft"><SelectValue /></SelectTrigger><SelectContent>{story.drafts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-2.5">
          <Check checked={lore} onChange={setLore} label="Add the full Lore Bible after the story" testid="export-include-lore" />
          <Check checked={hidden} onChange={setHidden} label="Include chapters hidden from the team" testid="export-include-hidden" />
        </div>
        <div className="rounded-md border border-[#2D3442] p-3" data-testid="export-appendices">
          <div className="lf-label mb-2">Appendices from the Lore Bible{commercial ? "" : " · Commercial"}</div>
          {commercial && entsLoading ? <p className="text-xs text-[#A8A090]" data-testid="export-appendix-loading">Loading Lore Bible entries…</p> : commercial ? <EntityMultiSelect entities={ents.items || ents} value={appx} onChange={setAppx} testid="export-appendix-select" />
            : <p className="text-xs text-[#A8A090]">Pick Lore Bible entries to print as appendices with the Commercial plan.</p>}
        </div>
        <DialogFooter><Button onClick={run} disabled={busy} data-testid="export-run">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Export {format.toUpperCase()}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
